import { getI18n } from 'src/i18n/i18n.middleware'
import { dynamic } from 'src/utils/ssr'
import { staticMeta, staticMetaLoader } from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/generator.preset.$sceneId'

const getClientModule = () =>
  import('src/clientRoutes/generator.preset.$sceneId')

export const handle = {
  name: 'generator/preset/:sceneId',
  track: ({ params }: { params: { sceneId?: string } }) => ({
    scene: 'chat-editing-preset',
    section: params.sceneId,
  }),
  showAppNav: true,
}

/**
 * SSR loader for chat editing scene page.
 * Source: https://github.com/troph-team/pixai/blob/main/packages/pixai-server/src/ssr/router.ts#L395-L417
 *
 * Tries Consul first (has metaTitle/metaDescription/metaImage from serverConfig),
 * falls back to GraphQL API when Consul is unavailable (e.g. local dev).
 */
export async function loader({ params, request, context }: Route.LoaderArgs) {
  const sceneId = params.sceneId
  if (!sceneId) return staticMetaLoader({ requestUrl: request.url })

  // Try Consul first (has serverConfig meta fields), fall back to GraphQL
  const { getSafeChatEditingScene } =
    await import('src/utils/ssr-meta/consul.server')
  const scene = await getSafeChatEditingScene(sceneId)

  if (scene) {
    const { t } = getI18n(context)
    // Use i18n keys from scene.metaTitle/metaDescription (from serverConfig)
    // Fallback to scene.title/description if meta keys not set
    const title = scene.metaTitle ? t(scene.metaTitle) : scene.title
    const description = scene.metaDescription
      ? t(scene.metaDescription)
      : scene.description
    const image = scene.metaImage || scene.images?.background || undefined

    return staticMetaLoader({
      requestUrl: request.url,
      title,
      description,
      image,
    })
  }

  // Fallback: fetch from GraphQL API (no metaTitle/metaDescription/metaImage,
  // but has title/description/images.background as i18n keys)
  const { fetchChatEditingSceneForSSR } =
    await import('src/utils/ssr-meta/graphql/chat.server')
  const gqlScene = await fetchChatEditingSceneForSSR(sceneId)
  if (!gqlScene) return staticMetaLoader({ requestUrl: request.url })

  const { t } = getI18n(context)
  return staticMetaLoader({
    requestUrl: request.url,
    title: t(gqlScene.title),
    description: t(gqlScene.description),
    image: gqlScene.images?.background || undefined,
  })
}

/** Meta function for SSR SEO. */
export function meta({ loaderData }: Route.MetaArgs) {
  return staticMeta(loaderData)
}

export default dynamic(getClientModule, { ssr: false })
